import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { DateRange } from 'react-day-picker';
import { X } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import DateRangeTextInputs from './DateRangeTextInputs';
import { Sheet, SheetContent, SheetHeaderBar, SheetTitle } from '@/components/ui/sheet';
import { EMPTY_FILTERS, countActiveFilters, type DocumentFilters } from '@hooks/useDocumentsQuery';
import { useDocumentFacets } from '@hooks/useDocumentFacets';
import FilterCheckboxGroup from './FilterCheckboxGroup';

interface DocumentFilterSidebarProps {
  open: boolean;
  /** Facet options are derived from these, so every option matches a document. */
  documents: any[];
  filters: DocumentFilters;
  onFilterChange: (filters: DocumentFilters) => void;
  onClose: () => void;
}

type ListKey = 'categories' | 'tags' | 'fileTypes' | 'uploadedBy';

const DocumentFilterSidebar: React.FC<DocumentFilterSidebarProps> = ({
  open,
  documents,
  filters,
  onFilterChange,
  onClose,
}) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<DocumentFilters>(filters);
  const [dateInputValid, setDateInputValid] = useState(true);
  // Remounting is the only reset that reaches the inputs' own text: clearing filters
  // leaves an already-empty range unchanged, so no prop-sync effect fires.
  const [dateInputsKey, setDateInputsKey] = useState(0);
  const resetDateInputs = () => {
    setDateInputsKey((key) => key + 1);
    setDateInputValid(true);
  };
  const facets = useDocumentFacets(documents);

  // Re-seed on open so a cancelled edit is discarded rather than carried over.
  useEffect(() => {
    if (open) {
      setDraft(filters);
      resetDateInputs();
    }
  }, [open, filters]);

  const toggle = (key: ListKey) => (value: string, checked: boolean) =>
    setDraft((current) => ({
      ...current,
      [key]: checked ? [...current[key], value] : current[key].filter((item) => item !== value),
    }));

  const setDateRange = (range: DateRange | undefined) =>
    setDraft((current) => ({
      ...current,
      dateRange: { start: range?.from ?? null, end: range?.to ?? null },
    }));

  const activeCount = countActiveFilters(draft);

  const sections: { id: string; label: string; count: number; content: React.ReactNode }[] = [
    {
      id: 'categories',
      label: t('documents.categories'),
      count: draft.categories.length,
      content: (
        <FilterCheckboxGroup
          options={facets.categories}
          formatLabel={(code) => facets.categoryLabels?.[code] ?? code}
          selected={draft.categories}
          onToggle={toggle('categories')}
        />
      ),
    },
    {
      id: 'tags',
      label: t('documents.tags'),
      count: draft.tags.length,
      content: (
        <FilterCheckboxGroup
          options={facets.tags}
          selected={draft.tags}
          onToggle={toggle('tags')}
        />
      ),
    },
    {
      id: 'dateRange',
      label: t('documents.dateRange'),
      count: draft.dateRange.start || draft.dateRange.end ? 1 : 0,
      content: (
        <div className="space-y-2">
          <DateRangePicker
            id="filter-date-range"
            value={
              draft.dateRange.start || draft.dateRange.end
                ? { from: draft.dateRange.start ?? undefined, to: draft.dateRange.end ?? undefined }
                : undefined
            }
            onChange={setDateRange}
          />
          <DateRangeTextInputs
            key={dateInputsKey}
            start={draft.dateRange.start}
            end={draft.dateRange.end}
            onChange={(range) => setDraft((current) => ({ ...current, dateRange: range }))}
            onValidityChange={setDateInputValid}
          />
        </div>
      ),
    },
    {
      id: 'uploadedBy',
      label: t('documents.uploadedBy'),
      count: draft.uploadedBy.length,
      content: (
        <FilterCheckboxGroup
          options={facets.uploadedBy}
          selected={draft.uploadedBy}
          onToggle={toggle('uploadedBy')}
        />
      ),
    },
    {
      id: 'fileTypes',
      label: t('documents.fileTypes'),
      count: draft.fileTypes.length,
      content: (
        <FilterCheckboxGroup
          options={facets.fileTypes}
          selected={draft.fileTypes}
          onToggle={toggle('fileTypes')}
          formatLabel={(value) => value.toUpperCase()}
        />
      ),
    },
  ];

  return (
    <Sheet open={open} onOpenChange={(next) => !next && onClose()}>
      <SheetContent
        side="left"
        className="revy-ui flex w-[320px] max-w-[85vw] flex-col gap-0 p-0 sm:max-w-[320px]"
      >
        <SheetHeaderBar>
          <SheetTitle className="text-base">{t('documents.filters')}</SheetTitle>
          {activeCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setDraft(EMPTY_FILTERS);
                resetDateInputs();
              }}
            >
              <X strokeWidth={2} />
              {t('common.clearAll')}
            </Button>
          )}
        </SheetHeaderBar>

        {activeCount > 0 && (
          <p className="border-b px-4 py-2 text-xs revy-figure text-muted-foreground">
            {t(activeCount === 1 ? 'documents.activeFilter' : 'documents.activeFilters', {
              count: activeCount,
            })}
          </p>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-4">
          <Accordion type="multiple" defaultValue={['categories', 'tags']} className="w-full">
            {sections.map(({ id, label, count, content }) => (
              <AccordionItem key={id} value={id}>
                <AccordionTrigger className="text-sm font-medium hover:no-underline">
                  <span className="flex items-center gap-2">
                    {label}
                    {count > 0 && (
                      <Badge variant="secondary" className="revy-figure">
                        {count}
                      </Badge>
                    )}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-4">{content}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        <div className="flex gap-2 border-t px-4 py-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!dateInputValid}
            onClick={() => {
              onFilterChange(draft);
              onClose();
            }}
            className="flex-1"
          >
            {t('documents.applyFilters')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DocumentFilterSidebar;
